import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { PageComponent } from 'src/app/shared/components/page/page.component';
import { University } from 'src/app/shared/models/university.model';
import { User } from 'src/app/shared/models/user.model';
import StrapiService from 'src/app/services/strapi.service';
import { Meta, Title } from '@angular/platform-browser';
import { PolicyDocument } from 'src/app/shared/models/policy.model';
import { Subscription } from 'rxjs';
import {
  IInternationalStudentPage,
  IStrapiDocument,
} from 'src/app/interfaces/strapi';
import { UniService } from 'src/app/services/uni.service';
import { ApiSearchResponse } from 'src/app/interfaces/api';
import { Router } from '@angular/router';
import { NgIf } from '@angular/common';
import { BreadcrumbsComponent } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { AuthService } from '../../../services/auth.service';
import { RenderMarkdownComponent } from '../../../shared/components/render-markdown/render-markdown.component';
import { MakeAClaimComponent } from '../make-a-claim/make-a-claim.component';
import { StudentLifeBannerComponent } from '../../../shared/components/student-life-banner/student-life-banner.component';

enum InternationalStudentPageEnum {
  WORDING = 'POLICY WORDING',
  IPID = 'IPID',
  SOI = 'STATEMENT OF INSURANCE',
}

@Component({
    selector: 'app-international-student-page',
    templateUrl: './international-student-page.component.html',
    styleUrls: ['./international-student-page.component.scss'],
  changeDetection:ChangeDetectionStrategy.OnPush,
    imports: [
        BreadcrumbsComponent,
        NgIf,
        RenderMarkdownComponent,
        MakeAClaimComponent,
        StudentLifeBannerComponent,
    ],
})
export class InternationalStudentPageComponent
  extends PageComponent
  implements OnInit, OnDestroy
{
  public override pageData: IInternationalStudentPage = <
    IInternationalStudentPage
  >{};

  public isLoading: boolean = false;

  public university: University = <University>{};

  public InternationalStudentPageEnum = InternationalStudentPageEnum;

  public policyWordingDocUrl: string = '';

  public policyIPIDDocUrl: string = '';

  public policyStatementOfInsuranceDocUrl: string = '';

  public userSubscription: Subscription | undefined;

  constructor(
    private authService: AuthService,
    protected override readonly titleService: Title,
    protected override readonly metaService: Meta,
    public override readonly strapi: StrapiService,
    public uni: UniService,
    public readonly router: Router,
    private readonly cdr: ChangeDetectorRef
  ) {
    super(titleService, metaService, strapi);
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.getUserUniversity();

    this.userSubscription = this.authService.getCurrentUser().subscribe({
      next: (user: User) => {
        if (user) {
          this.getUserUniversity();
        }
      },
    });
  }

  protected override parsePageData(page: IInternationalStudentPage): void {
    this.pageData = page;
    this.isLoading = false;
    super.parsePageData(page);
    this.cdr.markForCheck()
  }

  public getUserUniversity(): void {
    if (this.authService.currentUser?.university.uuid) {
      this.uni
        .getUniversityByUUID(this.authService.currentUser.university.uuid)
        .subscribe({
          next: (data: Partial<ApiSearchResponse>) => {
            this.university = <University>data.data;
            this.stringReplaceUniversityText();
            this.processPolicyDocument();
          },
          error: () => {
            this.getInternationalStudentData();
          },
          complete: () => {
            this.getInternationalStudentData();
            this.cdr.markForCheck();
          },
        });
    }
  }

  /**
   * get international student data from strapi
   */
  public getInternationalStudentData(): void {
    this.strapi.getInternationalStudentPage().subscribe({
      next: (
        internationalStudentData: IStrapiDocument<IInternationalStudentPage>
      ) => {
        this.parsePageData(internationalStudentData.data.attributes);
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  /**
   * Will string replace <name> with the users university for certain fields
   */
  public stringReplaceUniversityText(): void {
    this.university.pathway_provider_overview =
      this.university.pathway_provider_overview?.replaceAll(
        '<name>',
        this.university.name
      ) ?? '';

    this.university.pathway_provider_disclaimer =
      this.university.pathway_provider_disclaimer?.replaceAll(
        '<name>',
        this.university.name
      ) ?? '';
  }

  /**
   * process the policy documents to get urls
   * @param void
   * @returns void
   */
  public processPolicyDocument(): void {
    const policyDocs = this.university.policyDocuments;

    if (policyDocs?.length) {
      policyDocs.forEach((doc: PolicyDocument) => {
        switch (doc.type.toUpperCase()) {
          case InternationalStudentPageEnum.WORDING:
            this.policyWordingDocUrl = `${
              doc.file_url
            }?timestamp=${Date.now()}`;
            break;
          case InternationalStudentPageEnum.IPID:
            this.policyIPIDDocUrl = `${doc.file_url}?timestamp=${Date.now()}`;
            break;
          case InternationalStudentPageEnum.SOI:
            this.policyStatementOfInsuranceDocUrl = `${
              doc.file_url
            }?timestamp=${Date.now()}`;
            break;
          default:
            break;
        }
      });
    }
  }

  public policySummaryNavigate(): void {
    this.router
      .navigate(['app/international-student/policy-summary'])
      .finally(() => false);
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
  }
}
